import type { UUID } from "crypto";

import { useQuery } from "@tanstack/react-query";

import {
  CartErrorCode,
  type BankAccount,
  type Cart,
  type CartItem,
  type Coupon,
  type TenantConfig
} from "@rekaz/core";

import { useSDK } from "@/components/context/sdk-provider";

import { useSearchParams } from "./use-search-params";

export function useTenantConfig() {
  const { sdk, isInitialized } = useSDK();

  return useQuery<TenantConfig | null, Error>({
    queryKey: ["tenantConfig"],
    queryFn: () => sdk.getTenantConfig(),
    enabled: isInitialized,
    refetchOnWindowFocus: false,
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    retry: 1
  });
}

export function useBankAccounts() {
  const { sdk, isInitialized } = useSDK();

  return useQuery<BankAccount[] | null, Error>({
    queryKey: ["bankAccounts"],
    queryFn: () => sdk.cart.getBankAccounts(),
    enabled: isInitialized,
    refetchOnWindowFocus: false,
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: 1
  });
}

export function useCart(targetCartId?: UUID) {
  const { sdk, isInitialized } = useSDK();
  const { get } = useSearchParams();
  const cartId = get<UUID>("cartId");
  const callbackUrl = get<string>("callbackUrl");

  const effectiveCartId = targetCartId || cartId;

  return useQuery<Cart | null, Error>({
    queryKey: ["cart", effectiveCartId],
    queryFn: async (): Promise<Cart | null> => {
      if (!effectiveCartId) {
        throw new Error("Cart ID is required to fetch the cart");
      }
      try {
        const cartData = await sdk.cart.getCart(effectiveCartId);
        return cartData;
      } catch (error: any) {
        const errorCode = error?.code || error?.details?.error?.code;
        if (errorCode === CartErrorCode.CART_ALREADY_COMPLETED) {
          window.location.assign(callbackUrl || "/");
          return null;
        }
        throw error;
      }
    },
    enabled: isInitialized && !!effectiveCartId,
    refetchOnWindowFocus: false,
    retry: 1,
    retryDelay: 1000,
    staleTime: 2 * 60 * 1000,
    gcTime: 15 * 60 * 1000
  });
}

export function useValidateCoupon() {
  const { sdk } = useSDK();

  return {
    validateCoupon: (
      couponCode: string,
      cartItems: CartItem[]
    ): Promise<Coupon> => sdk.cart.validateCoupon(couponCode, cartItems)
  };
}

export async function getTermsAndConditions(tenant: string) {
  const response = await fetch(
    import.meta.env.REKAZ_API_URL +
      "/api/app/website-builder-old/terms-and-conditions",
    {
      method: "GET",
      headers: {
        __tenant: tenant,
        "Accept-Language": navigator.language
      }
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch terms: ${response.statusText}`);
  }

  const contentType = response.headers.get("content-type");

  if (contentType && contentType.includes("application/json")) {
    const res = await response.json();

    if (typeof res === "string") {
      return res;
    }

    if (res && typeof res === "object") {
      return (
        res.content ||
        res.terms ||
        res.html ||
        res.text ||
        res.body ||
        JSON.stringify(res)
      );
    }

    return null;
  } else {
    const htmlContent = await response.text();
    console.log(
      "Terms API response (HTML):",
      htmlContent.substring(0, 100) + "..."
    );
    return htmlContent;
  }
}
